import Logo from '@/assets/images/OMIKUJI Main Logo.png';
import { Button } from '@/components/common/Button';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/common/Form';
import { Input, PasswordInput } from '@/components/common/Input';
import { ROUTES } from '@/configs/constants';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { toastify } from '@/utils/toastify';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

export default observer(function LoginPage() {
    // hooks
    const { t } = useTranslation();
    const navigate = useNavigate();

    // store
    const { authStore: { login } } = useStore();

    // validate schema
    const validateSchema = yup.object().shape({
        email: yup
            .string()
            .required('form_error_validate.required'),
        password: yup.string().required('form_error_validate.required')
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            email: '',
            password: ''
        }
    });

    const [showPassword, setShowPassword] = useState<boolean>(false);

    // function
    const onSubmit = async (data: FormData) => {
        const { email, password } = data;
        const res = await flowResult(login({ email, password }));
        if (res && res?.ok) {
            toastify('success', t('messages.sign_in_success'));
            navigate(ROUTES.home.href);
        } else {
            toastify('error', res?.message);
        }
    };

    return (
        <div className='fixed inset-0 flex items-center justify-center z-50'>
            <div className='mx-auto flex flex-col justify-center space-y-6 px-6 py-8 w-[680px] rounded-xl border bg-card text-card-foreground shadow mt'>
                <img className='h-[70px] object-contain mb-14' src={Logo} alt='logo' />
                <h3 className='text-center text-[32px] font-bold'>{t('words_title.sign_in')}</h3>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className='space-y-4'>
                            <FormField
                                control={form.control}
                                name='email'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className='text-[16px] font-bold mb-[10px]' required>
                                            {t('words_title.username')}
                                        </FormLabel>
                                        <FormControl>
                                            <Input
                                                placeholder={t(
                                                    'words_title.username'
                                                )}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                            <FormField
                                control={form.control}
                                name='password'
                                render={({ field }) => (
                                    <FormItem>
                                        <FormLabel className='text-[16px] font-bold mb-[10px]' required>
                                            {t('words_title.password')}
                                        </FormLabel>
                                        <FormControl>
                                            <PasswordInput
                                                type={!showPassword ? 'password' : 'text'}
                                                placeholder={t(
                                                    'words_title.password'
                                                )}
                                                onToggleIcon={() => setShowPassword(!showPassword)}
                                                {...field}
                                            />
                                        </FormControl>
                                        <FormMessage />
                                    </FormItem>
                                )}
                            />
                        </div>
                        <div className='text-center mt-8'>
                            <Button type='submit' size='xl' variant='active' className='h-[3.25rem] w-full text-[16px] font-semibold flex items-center space-x-1.5'>
                                <p className='text-[16px] font-semibold'>{t('words_title.sign_in_btn')}</p>
                            </Button>
                        </div>
                    </form>
                </Form>
            </div>
        </div>
    );
});
